import React from 'react';
import { Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { FaHome, FaChartBar, FaCog, FaSignOutAlt, FaArrowLeft, FaBars, FaTimes, FaBuilding, FaExchangeAlt, FaXRay } from 'react-icons/fa';
import logo from '../assets/Alliedlogo.png';

const TopNav = ({ onBack, onHome, onSettings, onOutcomes, onImaging, onLogout, userPractices, activePractice, onPracticeChange, organization }) => {
  // userPractices is an array of practice names the user belongs to
  // activePractice is the currently selected practice
  // onPracticeChange is called when user selects a different practice
  
  const hasMultiplePractices = userPractices && userPractices.length > 1;
  const showBranding = organization !== 'development';
  
  return (
    <Disclosure as="nav" className="relative bg-white shadow-md outline outline-1 outline-black/5">
      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">
        <div className="flex h-20 sm:h-24 justify-between">
          {/* Left side - Brand */}
          <div className="flex items-center">
            {showBranding && (
              <div className="flex shrink-0 items-center">
                <img
                  src={logo}
                  alt="Allied OMS"
                  className="h-12 sm:h-14 w-auto"
                  fetchPriority="high"
                  loading="eager"
                />
              </div>
            )}
            <div className={showBranding ? "ml-4 hidden sm:block" : "hidden sm:block"}>
              <span className="text-lg font-semibold text-gray-900">Quality Improvement Reporting</span>
            </div>
          </div>

          {/* Desktop navigation */}
          <div className="hidden sm:ml-6 sm:flex sm:items-center sm:space-x-4">
            {/* Back button (when available) */}
            {onBack && (
              <button
                onClick={onBack}
                className="inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >
                <FaArrowLeft className="size-4" />
                Back
              </button>
            )}

            {/* Practice Switcher (when user belongs to multiple practices) */}
            {hasMultiplePractices && (
              <Menu as="div" className="relative">
                <MenuButton className="inline-flex items-center gap-x-2 rounded-md bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700 shadow-sm ring-1 ring-inset ring-indigo-200 hover:bg-indigo-100 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                  <FaBuilding className="size-4" />
                  <span className="max-w-[150px] truncate">{activePractice || 'Select Practice'}</span>
                  <FaExchangeAlt className="size-3 text-indigo-500" />
                </MenuButton>
                <MenuItems
                  transition
                  className="absolute right-0 z-10 mt-2 w-64 origin-top-right rounded-md bg-white py-1 shadow-lg outline outline-1 outline-black/5 transition data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                >
                  <div className="px-3 py-2 border-b border-gray-100">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Switch Practice</p>
                  </div>
                  {userPractices.map((practice) => (
                    <MenuItem key={practice}>
                      <button
                        onClick={() => onPracticeChange && onPracticeChange(practice)}
                        className={`flex w-full items-center gap-x-2 px-4 py-2 text-sm ${
                          practice === activePractice 
                            ? 'bg-indigo-50 text-indigo-700 font-medium' 
                            : 'text-gray-700 data-[focus]:bg-gray-100'
                        } data-[focus]:outline-none`}
                      >
                        <FaBuilding className={`size-4 ${practice === activePractice ? 'text-indigo-600' : 'text-gray-400'}`} />
                        <span className="truncate">{practice}</span>
                        {practice === activePractice && (
                          <span className="ml-auto text-xs text-indigo-500">Active</span>
                        )}
                      </button>
                    </MenuItem>
                  ))}
                </MenuItems>
              </Menu>
            )}

            {/* Profile dropdown */}
            <Menu as="div" className="relative ml-3">
              <MenuButton className="relative flex rounded-md bg-white p-2 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 hover:bg-gray-50">
                <span className="absolute -inset-1.5" />
                <span className="sr-only">Open user menu</span>
                <FaBars aria-hidden="true" className="size-5 text-gray-400" />
              </MenuButton>

              <MenuItems
                transition
                className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg outline outline-1 outline-black/5 transition data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
              >
                {onHome && (
                  <MenuItem>
                    <button
                      onClick={onHome}
                      className="flex w-full items-center gap-x-2 px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:outline-none"
                    >
                      <FaHome className="size-4" />
                      Home
                    </button>
                  </MenuItem>
                )}
                {onOutcomes && (
                  <MenuItem>
                    <button
                      onClick={onOutcomes}
                      className="flex w-full items-center gap-x-2 px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:outline-none"
                    >
                      <FaChartBar className="size-4" />
                      Dashboard
                    </button>
                  </MenuItem>
                )}
                {onImaging && (
                  <MenuItem>
                    <button
                      onClick={onImaging}
                      className="flex w-full items-center gap-x-2 px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:outline-none"
                    >
                      <FaXRay className="size-4" />
                      DICOM Viewer
                    </button>
                  </MenuItem>
                )}
                {onSettings && (
                  <MenuItem>
                    <button
                      onClick={onSettings}
                      className="flex w-full items-center gap-x-2 px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:outline-none"
                    >
                      <FaCog className="size-4" />
                      Settings
                    </button>
                  </MenuItem>
                )}
                {onLogout && (
                  <MenuItem>
                    <button
                      onClick={onLogout}
                      className="flex w-full items-center gap-x-2 px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:outline-none"
                    >
                      <FaSignOutAlt className="size-4" />
                      Logout
                    </button>
                  </MenuItem>
                )}
              </MenuItems>
            </Menu>
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center sm:hidden">
            {onBack && (
              <button
                onClick={onBack}
                className="mr-2 inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >
                <FaArrowLeft className="size-5" />
              </button>
            )}
            <DisclosureButton className="group relative inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
              <span className="absolute -inset-0.5" />
              <span className="sr-only">Open main menu</span>
              <FaBars aria-hidden="true" className="block size-6 group-data-[open]:hidden" />
              <FaTimes aria-hidden="true" className="hidden size-6 group-data-[open]:block" />
            </DisclosureButton>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <DisclosurePanel className="sm:hidden">
        <div className="space-y-1 border-t border-gray-200 pb-3 pt-2">
          {/* Practice Switcher (mobile) */}
          {hasMultiplePractices && (
            <div className="border-b border-gray-200 pb-2 mb-2">
              <div className="px-4 py-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Active Practice</p>
                <p className="text-sm font-medium text-indigo-700 mt-1">{activePractice}</p>
              </div>
              <div className="px-4">
                <select
                  value={activePractice || ''}
                  onChange={(e) => onPracticeChange && onPracticeChange(e.target.value)}
                  className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                >
                  {userPractices.map((practice) => (
                    <option key={practice} value={practice}>{practice}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
          {onHome && (
            <DisclosureButton
              as="button"
              onClick={onHome}
              className="block w-full border-l-4 border-transparent py-2 pl-3 pr-4 text-left text-base font-medium text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700"
            >
              <div className="flex items-center gap-x-2">
                <FaHome className="size-4" />
                Home
              </div>
            </DisclosureButton>
          )}
          {onOutcomes && (
            <DisclosureButton
              as="button"
              onClick={onOutcomes}
              className="block w-full border-l-4 border-transparent py-2 pl-3 pr-4 text-left text-base font-medium text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700"
            >
              <div className="flex items-center gap-x-2">
                <FaChartBar className="size-4" />
                Dashboard
              </div>
            </DisclosureButton>
          )}
          {onImaging && (
            <DisclosureButton
              as="button"
              onClick={onImaging}
              className="block w-full border-l-4 border-transparent py-2 pl-3 pr-4 text-left text-base font-medium text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700"
            >
              <div className="flex items-center gap-x-2">
                <FaXRay className="size-4" />
                DICOM Viewer
              </div>
            </DisclosureButton>
          )}
          {onSettings && (
            <DisclosureButton
              as="button"
              onClick={onSettings}
              className="block w-full border-l-4 border-transparent py-2 pl-3 pr-4 text-left text-base font-medium text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700"
            >
              <div className="flex items-center gap-x-2">
                <FaCog className="size-4" />
                Settings
              </div>
            </DisclosureButton>
          )}
          {onLogout && (
            <DisclosureButton
              as="button"
              onClick={onLogout}
              className="block w-full border-l-4 border-transparent py-2 pl-3 pr-4 text-left text-base font-medium text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700"
            >
              <div className="flex items-center gap-x-2">
                <FaSignOutAlt className="size-4" />
                Logout
              </div>
            </DisclosureButton>
          )}
        </div>
      </DisclosurePanel>
    </Disclosure>
  );
};

export default TopNav;